// safety/safety-walks/pages/SafetyWalksDashboardPage.tsx
// 6 KPI cards + 4 charts (Observations By Status donut, Monthly Trend line,
// By Project bar, By Responsible Person progress) + overdue alert banner.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TriangleAlert, Loader2, ArrowRight } from 'lucide-react';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../../constants/permissions.constants';
import AccessRestricted from '../../../../components/AccessRestricted';
import {
    PieChart, Pie, Cell, ResponsiveContainer,
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    BarChart, Bar,
} from 'recharts';
import { getWalkStats } from '../../../../utils/safety-walks-api';
import type { WalkStats } from '../../../../types/safety-walk.types';

const DONUT_COLORS: Record<string, string> = {
    Open: '#3b82f6',
    'In Progress': '#f59e0b',
    'Pending Verification': '#8b5cf6',
    Closed: '#10b981',
};

const KPI_ITEMS: { key: keyof WalkStats['kpis']; label: string; sub?: (k: WalkStats['kpis']) => string | null; accent: string }[] = [
    { key: 'openObservations', label: 'Open Observations', accent: 'text-blue-600' },
    { key: 'overdueObservations', label: 'Overdue Observations', sub: () => 'Action required', accent: 'text-red-600' },
    { key: 'dueThisWeek', label: 'Due This Week', sub: (k) => (k.dueThisWeek > 0 ? `${k.dueThisWeek} due tomorrow` : null), accent: 'text-amber-600' },
    { key: 'walksThisMonth', label: 'Walks This Month', sub: (k) => (k.walksLastMonth > 0 ? `${k.walksThisMonth - k.walksLastMonth >= 0 ? '+' : ''}${k.walksThisMonth - k.walksLastMonth} vs last month` : null), accent: 'text-violet-600' },
    { key: 'avgCloseOutDays', label: 'Avg Close-Out Time', sub: () => 'Target: 5 days', accent: 'text-emerald-600' },
    { key: 'highRisk', label: 'High-Risk Observations', sub: () => 'Requires escalation', accent: 'text-orange-600' },
];

export const SafetyWalksDashboardPage: React.FC = () => {
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const [stats, setStats] = useState<WalkStats | null>(null);
    const [loading, setLoading] = useState(true);

    const canView = ability.can(ACTIONS.READ, RESOURCES.SAFETY_DASHBOARD) || ability.can(ACTIONS.READ, RESOURCES.SAFETY_WALK_LIST);

    const fetchStats = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getWalkStats();
            setStats(res.stats || null);
        } catch {
            // api util toasts
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canView) fetchStats();
    }, [fetchStats, canView]);

    const kpis = stats?.kpis;
    const charts = stats?.charts;

    if (!canView) {
        return <AccessRestricted accessMessage="You don't have permission to access the Safety Walk Dashboard." />;
    }

    const statusDonut = charts
        ? Object.entries(charts.byStatus).map(([name, value]) => ({ name, value }))
        : [];

    return (
        <div className="pb-10 px-0 sm:px-4 lg:px-0 max-w-[1500px] mx-auto w-full">
            <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-medium">Safety Walks</h1>
                    <p className="text-sm text-muted-foreground mt-1">Site safety walk observations and corrective actions</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate('/safety/walks/register')}
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent"
                    >
                        Walk Register
                    </button>
                    <button
                        onClick={() => navigate('/safety/walks/observations')}
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent"
                    >
                        Observations
                    </button>
                    <button
                        onClick={() => navigate('/safety/walks/new')}
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                    >
                        New Safety Walk
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-24">
                    <Loader2 size={24} className="animate-spin text-muted-foreground" />
                </div>
            ) : !stats ? (
                <div className="rounded-xl border border-border bg-card py-16 text-center text-muted-foreground">
                    Unable to load dashboard data
                </div>
            ) : (
                <>
                    {/* Overdue alert banner */}
                    {(kpis?.overdueObservations ?? 0) > 0 && (
                        <div className="flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 mb-6">
                            <div className="flex items-center gap-2 text-sm text-red-700">
                                <TriangleAlert size={18} />
                                <span>
                                    <strong>{kpis?.overdueObservations}</strong> observation(s) are overdue — immediate attention required.
                                </span>
                            </div>
                            <button
                                onClick={() => navigate('/safety/walks/observations?tab=overdue')}
                                className="inline-flex items-center gap-1 text-sm font-medium text-red-700 hover:underline"
                            >
                                View Overdue <ArrowRight size={14} />
                            </button>
                        </div>
                    )}

                    {/* KPI cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
                        {KPI_ITEMS.map((item) => {
                            const value = kpis?.[item.key];
                            const display = typeof value === 'number' && item.key === 'avgCloseOutDays'
                                ? (value === null ? '—' : `${value}d`)
                                : (value ?? '—');
                            return (
                                <div key={item.key} className="rounded-xl border border-border bg-card p-4">
                                    <div className={`text-2xl font-semibold ${item.accent}`}>{display}</div>
                                    <div className="text-xs text-muted-foreground mt-1">{item.label}</div>
                                    {item.sub && item.sub(kpis!) && (
                                        <div className="text-[11px] text-muted-foreground/80 mt-1">{item.sub(kpis!)}</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Charts row 1 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        <div className="rounded-xl border border-border bg-card p-5">
                            <h3 className="text-sm font-semibold mb-4">Observations By Status</h3>
                            <div className="h-[240px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={statusDonut} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                                            {statusDonut.map((entry) => (
                                                <Cell key={entry.name} fill={DONUT_COLORS[entry.name] || '#94a3b8'} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value: number) => [value, 'Observations']} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="flex flex-wrap justify-center gap-4 mt-2">
                                {statusDonut.map((entry) => (
                                    <div key={entry.name} className="flex items-center gap-1.5 text-xs">
                                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: DONUT_COLORS[entry.name] || '#94a3b8' }} />
                                        <span className="text-muted-foreground">{entry.name}</span>
                                        <span className="font-medium">{entry.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="rounded-xl border border-border bg-card p-5">
                            <h3 className="text-sm font-semibold mb-4">Monthly Trend</h3>
                            <div className="h-[240px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={charts?.monthlyTrend ?? []}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                        <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} dy={8} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} allowDecimals={false} />
                                        <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                                        <Line type="monotone" dataKey="observations" name="Observations" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3 }} />
                                        <Line type="monotone" dataKey="closed" name="Closed" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    {/* Charts row 2 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="rounded-xl border border-border bg-card p-5">
                            <h3 className="text-sm font-semibold mb-4">Observations By Project</h3>
                            <div className="h-[220px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={charts?.byProject ?? []}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                        <XAxis dataKey="project" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} dy={8} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} allowDecimals={false} />
                                        <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                                        <Bar dataKey="count" name="Observations" fill="#6366f1" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="rounded-xl border border-border bg-card p-5">
                            <h3 className="text-sm font-semibold mb-4">Observations By Responsible Person</h3>
                            <div className="space-y-4">
                                {(charts?.byResponsiblePerson ?? []).map((row) => {
                                    const max = Math.max(1, ...(charts?.byResponsiblePerson ?? []).map((r) => r.count));
                                    return (
                                        <div key={row.name}>
                                            <div className="flex items-center justify-between text-sm mb-1">
                                                <span className="font-medium">{row.name}</span>
                                                <span className="text-muted-foreground">{row.count}</span>
                                            </div>
                                            <div className="h-2 rounded-full bg-muted overflow-hidden">
                                                <div className="h-full bg-primary rounded-full" style={{ width: `${(row.count / max) * 100}%` }} />
                                            </div>
                                        </div>
                                    );
                                })}
                                {(charts?.byResponsiblePerson ?? []).length === 0 && (
                                    <p className="text-sm text-muted-foreground py-8 text-center">No data</p>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
